import { Box, Button, Modal, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { handleLogOut } from "./UserMenu";

type WarningModalProps = {
  open: boolean;
  onClose: () => void;
  onStayLoggedIn: () => void;
  warningDuration: number; // Warning duration in seconds
  onStateChange?: (state: string, data?: any) => void;
};

const LogoutWarningModal = React.forwardRef<HTMLDivElement, WarningModalProps>(
  ({ open, onClose, onStayLoggedIn, onStateChange, warningDuration }, ref) => {
    const [timeLeft, setTimeLeft] = useState<number>(warningDuration);
    const history = useHistory();

    useEffect(() => {
      let intervalId: NodeJS.Timeout;
      
      if (open) {
        // Initialize with the warning duration from props
        setTimeLeft(warningDuration);
        
        intervalId = setInterval(() => {
          setTimeLeft(prevTime => {
            const newTime = prevTime - 1;
            if (newTime <= 0) {
              onClose(); // This will trigger logout in the parent hook
            }
            return Math.max(0, newTime);
          });
        }, 1000);
      }

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [open, warningDuration, onClose]);

    const handleStayLoggedIn = () => {
      setTimeLeft(warningDuration); // Reset to the warning duration
      onStayLoggedIn();
    };

    const formatTime = (seconds: number): string => {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
    };

    return (
      <Modal open={open} onClose={onClose}>
        <Box
          ref={ref}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            outline: "none",
          }}
        >
          <Typography variant="h6" component="h2">
            Inactivity Warning
          </Typography>
          <Typography sx={{ mt: 2 }}>
            You will be logged out in {formatTime(timeLeft)} due to inactivity.
          </Typography>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-start", gap: "1rem" }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleStayLoggedIn} 
              className="primary-color-button"
            >
              Stay Logged In
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => handleLogOut(history, onStateChange)} 
              sx={{
                bgcolor: "#C41E3A",
                color: '#fff',
                border: "1px solid #C41E3A",
                "&:hover": {
                  color: "#C41E3A",
                  background: "transparent",
                  border: "1px solid #C41E3A",
                }
              }}
            >
              Logout Now
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
);

export default LogoutWarningModal;